import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiFetch from '../../../../utils/api';
import Preloader from '../../../../components/Preloader/Preloader';
import './AdminStyles.css';

const STATUS_COLORS = {
  completed:   { bg: 'var(--awp-success-soft)', color: 'var(--awp-success)' },
  watching:    { bg: 'var(--awp-info-soft)',    color: 'var(--awp-info)' },
  pending:     { bg: 'var(--awp-warning-soft)', color: 'var(--awp-warning)' },
  locked:      { bg: 'var(--awp-danger-soft)',  color: 'var(--awp-danger)' },
  not_started: { bg: 'var(--awp-surface-2)',    color: 'var(--awp-text-muted)' },
};

const SCHEDULE_TYPES = [
  { value: 'daily',   label: 'Daily',   hint: 'Single day' },
  { value: 'weekly',  label: 'Weekly',  hint: '7-day window' },
  { value: 'monthly', label: 'Monthly', hint: 'Full calendar month' },
  { value: 'custom',  label: 'Custom',  hint: 'Any date range' },
];

const TYPE_COLORS = {
  daily:   { bg: 'var(--awp-warning-soft)', color: 'var(--awp-warning)' },
  weekly:  { bg: 'var(--awp-info-soft)',    color: 'var(--awp-info)' },
  monthly: { bg: 'var(--awp-accent-soft)',  color: 'var(--awp-accent)' },
  custom:  { bg: 'var(--awp-success-soft)', color: 'var(--awp-success)' },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDuration(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtDate(str) {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

function fmtDateRange(schedule) {
  if (!schedule) return '';
  const { schedule_type, start_date, end_date } = schedule;
  if (schedule_type === 'daily') return fmtDate(start_date);
  if (schedule_type === 'monthly') {
    const [y, m] = start_date.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  }
  return `${fmtDate(start_date)} → ${fmtDate(end_date)}`;
}

function addDays(dateStr, days) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

// ── VideoPoolStrip ────────────────────────────────────────────────────────────
function VideoPoolStrip({ videos }) {
  if (!videos || videos.length === 0) {
    return <p style={{ fontSize: 13, color: 'var(--awp-text-subtle)', margin: 0 }}>No videos in pool.</p>;
  }
  const shown = videos.slice(0, 4);
  const rest  = videos.length - shown.length;
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        {shown.map(v => (
          <div key={v.id} style={{ width: 72, flexShrink: 0 }}>
            {v.thumbnail_url
              ? <img src={v.thumbnail_url} alt={v.title}
                  style={{ width: 72, height: 42, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              : <div style={{ width: 72, height: 42, borderRadius: 4, background: 'var(--awp-surface-2)' }} />
            }
          </div>
        ))}
        {rest > 0 && (
          <div style={{
            width: 72, height: 42, borderRadius: 4, background: 'var(--awp-surface-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: 'var(--awp-text-muted)', flexShrink: 0,
          }}>
            +{rest}
          </div>
        )}
      </div>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {shown.map(v => (
          <li key={v.id} style={{ fontSize: 12, color: 'var(--awp-text)', marginBottom: 2 }}>
            {v.title.length > 55 ? v.title.slice(0, 52) + '…' : v.title}
            <span style={{ color: 'var(--awp-text-subtle)', marginLeft: 4 }}>({fmtDuration(v.duration_seconds)})</span>
          </li>
        ))}
        {rest > 0 && <li style={{ fontSize: 12, color: 'var(--awp-text-subtle)' }}>…and {rest} more</li>}
      </ul>
    </div>
  );
}

// ── ScheduleCard ──────────────────────────────────────────────────────────────
function ScheduleCard({ schedule, label, labelBg, labelColor }) {
  const tc = TYPE_COLORS[schedule.schedule_type] || TYPE_COLORS.custom;
  return (
    <div style={{ flex: '1 1 280px', background: 'var(--awp-surface-2)', borderRadius: 10, padding: 16, border: '1px solid var(--awp-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--awp-text-subtle)' }}>{label}</span>
          <span style={{ fontSize: 11, background: tc.bg, color: tc.color, padding: '2px 8px', borderRadius: 12, fontWeight: 700, textTransform: 'capitalize' }}>
            {schedule.schedule_type}
          </span>
        </div>
        <span style={{ fontSize: 11, background: labelBg, color: labelColor, padding: '2px 8px', borderRadius: 12, fontWeight: 700 }}>
          {schedule.video_count} video{schedule.video_count !== 1 ? 's' : ''}
          {schedule.duration_filter_seconds > 0 && ` · ${fmtDuration(schedule.duration_filter_seconds)} watch`}
        </span>
      </div>
      <p style={{ fontSize: 12, color: 'var(--awp-text-subtle)', margin: '0 0 8px' }}>{fmtDateRange(schedule)}</p>
      <VideoPoolStrip videos={schedule.videos} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const AdminVideoManagement = () => {
  // ── Scrape ────────────────────────────────────────────────────────────────
  const [channelUrl, setChannelUrl]     = useState('');
  const [scrapeStatus, setScrapeStatus] = useState({ status: 'idle', count: 0, error: '' });
  const [scraping, setScraping]         = useState(false);
  const pollingRef                      = useRef(null);

  // ── Video pool list ───────────────────────────────────────────────────────
  const [videos, setVideos]             = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [durationFilter, setDurationFilter] = useState(0);
  const [videoPage, setVideoPage]       = useState(1);
  const [videoPagination, setVideoPagination] = useState(null);

  // ── Multi-select ──────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds]   = useState(new Set());

  // ── Schedule creation form ────────────────────────────────────────────────
  const [scheduleType, setScheduleType] = useState('weekly');
  const [schedStartDate, setSchedStartDate] = useState('');
  const [schedEndDate, setSchedEndDate]   = useState('');
  const [schedMonth, setSchedMonth]       = useState('');

  // ── Schedules display ─────────────────────────────────────────────────────
  const [activeSchedule, setActiveSchedule]     = useState(null);
  const [upcomingSchedules, setUpcomingSchedules] = useState([]);
  const [scheduleLoading, setScheduleLoading]   = useState(false);

  // ── Duration filter ───────────────────────────────────────────────────────
  const [filterSetting, setFilterSetting] = useState('');
  const [filterLoading, setFilterLoading] = useState(true);
  const [savingFilter, setSavingFilter]   = useState(false);

  // ── Assignments ───────────────────────────────────────────────────────────
  const [assignments, setAssignments]               = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsSchedule, setAssignmentsSchedule] = useState(null);
  const [totalSlots, setTotalSlots]                 = useState(0);

  // ── Delete confirmation ───────────────────────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, title } | 'bulk' | null
  const [deleting, setDeleting] = useState(false);

  // ── Feedback ──────────────────────────────────────────────────────────────
  const [msg, setMsg] = useState({ text: '', ok: true });
  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg({ text: '', ok: true }), 5000);
  };

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchVideos = useCallback((page = 1) => {
    setVideosLoading(true);
    apiFetch(`/api/admin/videos?duration_filter=${durationFilter}&page=${page}&limit=100`)
      .then(r => r.json())
      .then(d => { setVideos(d.videos || []); setVideoPagination(d.pagination || null); setVideosLoading(false); })
      .catch(() => setVideosLoading(false));
  }, [durationFilter]);

  const fetchSchedule = useCallback(() => {
    setScheduleLoading(true);
    apiFetch('/api/admin/videos/schedule')
      .then(r => r.json())
      .then(d => {
        setActiveSchedule(d.active || null);
        setUpcomingSchedules(d.upcoming || []);
        setScheduleLoading(false);
      })
      .catch(() => setScheduleLoading(false));
  }, []);

  const fetchDurationFilter = useCallback(() => {
    setFilterLoading(true);
    apiFetch('/api/admin/settings/duration-filter')
      .then(r => r.json())
      .then(d => { if (d.success) setFilterSetting(String(d.duration_filter_seconds)); setFilterLoading(false); })
      .catch(() => setFilterLoading(false));
  }, []);

  const fetchAssignments = useCallback(() => {
    setAssignmentsLoading(true);
    apiFetch('/api/admin/videos/week-assignments')
      .then(r => r.json())
      .then(d => {
        setAssignments(d.assignments || []);
        setAssignmentsSchedule(d.schedule || null);
        setTotalSlots(d.total_slots_available || 0);
        setAssignmentsLoading(false);
      })
      .catch(() => setAssignmentsLoading(false));
  }, []);

  useEffect(() => { fetchVideos(1); setVideoPage(1); }, [fetchVideos]);
  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);
  useEffect(() => { fetchDurationFilter(); }, [fetchDurationFilter]);
  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  // ── Scrape polling ────────────────────────────────────────────────────────
  const startPolling = () => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      try {
        const res = await apiFetch('/api/admin/videos/scrape-status');
        const d = await res.json();
        setScrapeStatus(d);
        if (d.status === 'done') {
          clearInterval(pollingRef.current); pollingRef.current = null;
          setScraping(false); fetchVideos(1); setVideoPage(1);
          flash(`Scrape complete — ${d.count} videos imported.`);
        } else if (d.status === 'error') {
          clearInterval(pollingRef.current); pollingRef.current = null;
          setScraping(false);
          flash(`Scrape error: ${d.error}`, false);
        }
      } catch { /* keep polling */ }
    }, 3000);
  };
  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  // ── Schedule form validation & payload build ─────────────────────────────
  const buildSchedulePayload = () => {
    let start_date, end_date;

    if (scheduleType === 'daily') {
      if (!schedStartDate) { flash('Please select a date.', false); return null; }
      start_date = schedStartDate;
    } else if (scheduleType === 'weekly') {
      if (!schedStartDate) { flash('Please select a start date.', false); return null; }
      start_date = schedStartDate;
    } else if (scheduleType === 'monthly') {
      if (!schedMonth) { flash('Please select a month.', false); return null; }
      start_date = schedMonth + '-01';
    } else { // custom
      if (!schedStartDate || !schedEndDate) {
        flash('Please select both a start and end date.', false); return null;
      }
      if (schedEndDate < schedStartDate) {
        flash('End date must be on or after start date.', false); return null;
      }
      start_date = schedStartDate;
      end_date   = schedEndDate;
    }

    return { schedule_type: scheduleType, start_date, ...(end_date ? { end_date } : {}) };
  };

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleScrape = async () => {
    if (!channelUrl.trim()) { flash('Please enter a YouTube channel URL.', false); return; }
    setScraping(true);
    setScrapeStatus({ status: 'running', count: 0, error: '' });
    try {
      const res = await apiFetch('/api/admin/videos/scrape', {
        method: 'POST',
        body: JSON.stringify({ channel_url: channelUrl.trim() }),
      });
      const d = await res.json();
      if (!d.success) { flash(d.message || 'Failed to start scrape.', false); setScraping(false); return; }
      startPolling();
    } catch { flash('Network error starting scrape.', false); setScraping(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (videos.every(v => selectedIds.has(v.id))) {
      setSelectedIds(prev => { const n = new Set(prev); videos.forEach(v => n.delete(v.id)); return n; });
    } else {
      setSelectedIds(prev => { const n = new Set(prev); videos.forEach(v => n.add(v.id)); return n; });
    }
  };

  const allPageSelected = videos.length > 0 && videos.every(v => selectedIds.has(v.id));

  const handleSchedule = async (auto) => {
    const base = buildSchedulePayload();
    if (!base) return;

    if (!auto && selectedIds.size === 0) {
      flash('Select at least one video first.', false); return;
    }

    const payload = {
      ...base,
      ...(auto ? { auto: true } : { video_ids: [...selectedIds] }),
    };

    try {
      const res = await apiFetch('/api/admin/videos/schedule', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (d.success) {
        const n = d.schedule.video_count;
        flash(`${n} video${n !== 1 ? 's' : ''} scheduled for ${fmtDateRange(d.schedule)}.`);
        if (!auto) setSelectedIds(new Set());
        fetchSchedule();
        fetchAssignments();
      } else {
        flash(d.message || 'Could not schedule.', false);
      }
    } catch { flash('Network error.', false); }
  };

  const handleDeleteOne = async () => {
    if (!confirmDelete || confirmDelete === 'bulk') return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/admin/videos/${confirmDelete.id}/delete`, { method: 'DELETE' });
      const d = await res.json();
      if (d.success) {
        flash(d.message);
        setSelectedIds(prev => { const n = new Set(prev); n.delete(confirmDelete.id); return n; });
        fetchVideos(videoPage);
      } else {
        flash(d.message || 'Could not delete video.', false);
      }
    } catch { flash('Network error.', false); }
    setDeleting(false);
    setConfirmDelete(null);
  };

  const handleBulkDelete = async () => {
    if (confirmDelete !== 'bulk' || selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const res = await apiFetch('/api/admin/videos/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids: [...selectedIds] }),
      });
      const d = await res.json();
      if (d.success) {
        flash(d.message);
        setSelectedIds(new Set());
        fetchVideos(1);
        setVideoPage(1);
      } else {
        flash(d.message || 'Could not delete videos.', false);
      }
    } catch { flash('Network error.', false); }
    setDeleting(false);
    setConfirmDelete(null);
  };

  const handleSaveDurationFilter = async () => {
    const val = parseInt(filterSetting, 10);
    if (isNaN(val) || val < 0 || val > 600) {
      flash('Watch duration must be 0–600 seconds.', false);
      return;
    }
    setSavingFilter(true);
    try {
      const res = await apiFetch('/api/admin/settings/duration-filter', {
        method: 'PUT',
        body: JSON.stringify({ duration_seconds: val }),
      });
      const d = await res.json();
      if (d.success) flash(`Watch duration saved: ${val === 0 ? 'full video' : val + 's'}.`);
      else flash(d.message || 'Failed to save.', false);
    } catch { flash('Network error.', false); }
    setSavingFilter(false);
  };

  // ── Date range preview label ──────────────────────────────────────────────
  const datePreview = (() => {
    if (scheduleType === 'daily' && schedStartDate)
      return fmtDate(schedStartDate);
    if (scheduleType === 'weekly' && schedStartDate)
      return `${fmtDate(schedStartDate)} → ${fmtDate(addDays(schedStartDate, 6))}`;
    if (scheduleType === 'monthly' && schedMonth) {
      const [y, m] = schedMonth.split('-');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `All of ${months[parseInt(m, 10) - 1]} ${y}`;
    }
    if (scheduleType === 'custom' && schedStartDate && schedEndDate)
      return `${fmtDate(schedStartDate)} → ${fmtDate(schedEndDate)}`;
    return null;
  })();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="admin-page">
      <div className="admin-breadcrumb">🏠 / Admin / Video Management</div>
      <h2 className="admin-title">Ad Video Management</h2>

      {msg.text && (
        <div className="admin-card" style={{
          background: msg.ok ? 'var(--awp-success-soft)' : 'var(--awp-danger-soft)',
          border: `1px solid var(${msg.ok ? '--awp-success' : '--awp-danger'})`,
          marginBottom: 16,
        }}>
          <div className="admin-card-body" style={{ padding: '10px 16px', color: `var(${msg.ok ? '--awp-success' : '--awp-danger'})`, fontSize: 14 }}>
            {msg.text}
          </div>
        </div>
      )}

      {/* ── Section 1: Scrape ─────────────────────────────────────────── */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div className="admin-card-header"><span>Scrape YouTube Channel</span></div>
        <div className="admin-card-body">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 300px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: 'var(--awp-text-muted)' }}>
                YouTube Channel URL
              </label>
              <input
                type="url" value={channelUrl} onChange={e => setChannelUrl(e.target.value)}
                placeholder="https://www.youtube.com/@ChannelName" disabled={scraping}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--awp-border)', fontSize: 14, boxSizing: 'border-box', background: 'var(--awp-surface)', color: 'var(--awp-text)' }}
              />
            </div>
            <button className="admin-btn admin-btn--edit" onClick={handleScrape} disabled={scraping} style={{ padding: '9px 20px', fontSize: 14 }}>
              {scraping ? 'Scraping…' : 'Scrape Channel'}
            </button>
          </div>
          {scraping && (
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 18, height: 18, border: '3px solid var(--awp-border)', borderTop: '3px solid var(--awp-accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <span style={{ fontSize: 13, color: 'var(--awp-text-muted)' }}>Scraping… {scrapeStatus.count} videos imported so far</span>
            </div>
          )}
          {scrapeStatus.status === 'done' && !scraping && scrapeStatus.count > 0 && (
            <p style={{ marginTop: 10, fontSize: 13, color: 'var(--awp-success)' }}>Last scrape: {scrapeStatus.count} videos imported.</p>
          )}
          {scrapeStatus.status === 'error' && !scraping && (
            <p style={{ marginTop: 10, fontSize: 13, color: 'var(--awp-danger)' }}>Error: {scrapeStatus.error}</p>
          )}
        </div>
      </div>

      {/* ── Section 2: Watch duration setting ────────────────────────── */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div className="admin-card-header"><span>Global Watch Duration</span></div>
        <div className="admin-card-body">
          <p style={{ fontSize: 13, color: 'var(--awp-text-muted)', marginTop: 0, marginBottom: 12 }}>
            How many seconds each user watches per session. Every user gets a unique start
            position so no two users see the same segment. Set to <strong>0</strong> to play
            the full video. Applies from the next schedule created.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {filterLoading ? <span style={{ fontSize: 13, color: 'var(--awp-text-subtle)' }}>Loading…</span> : (
              <>
                <input
                  type="number" min="0" max="600" step="1"
                  value={filterSetting} onChange={e => setFilterSetting(e.target.value)}
                  placeholder="e.g. 60"
                  style={{ width: 100, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--awp-border)', fontSize: 14, background: 'var(--awp-surface)', color: 'var(--awp-text)' }}
                />
                <span style={{ fontSize: 13, color: 'var(--awp-text-muted)' }}>seconds (0 = full video)</span>
                {filterSetting !== '' && parseInt(filterSetting, 10) > 0 && (
                  <span style={{ fontSize: 13, color: 'var(--awp-accent)', fontWeight: 600 }}>
                    = {fmtDuration(parseInt(filterSetting, 10))}
                  </span>
                )}
                <button className="admin-btn admin-btn--approve" onClick={handleSaveDurationFilter} disabled={savingFilter} style={{ padding: '9px 20px', fontSize: 14 }}>
                  {savingFilter ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Section 3: Schedule creation ─────────────────────────────── */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div className="admin-card-header"><span>Create / Update Schedule</span></div>
        <div className="admin-card-body">

          {/* Type selector */}
          <div style={{ marginBottom: 18 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--awp-text-muted)', margin: '0 0 8px' }}>Schedule Type</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SCHEDULE_TYPES.map(t => {
                const active = scheduleType === t.value;
                const tc = TYPE_COLORS[t.value];
                return (
                  <button
                    key={t.value}
                    onClick={() => setScheduleType(t.value)}
                    style={{
                      padding: '7px 18px',
                      borderRadius: 20,
                      border: `2px solid ${active ? tc.color : 'var(--awp-border)'}`,
                      background: active ? tc.bg : 'var(--awp-surface)',
                      color: active ? tc.color : 'var(--awp-text-muted)',
                      fontWeight: active ? 700 : 400,
                      cursor: 'pointer',
                      fontSize: 13,
                      transition: 'all 0.15s',
                    }}
                    title={t.hint}
                  >
                    {t.label}
                    <span style={{ fontSize: 11, marginLeft: 5, opacity: 0.7 }}>({t.hint})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date inputs */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
            {(scheduleType === 'daily' || scheduleType === 'weekly') && (
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--awp-text-muted)', marginBottom: 4 }}>
                  {scheduleType === 'daily' ? 'Date' : 'Start Date'}
                </label>
                <input
                  type="date" value={schedStartDate} onChange={e => setSchedStartDate(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--awp-border)', fontSize: 14, background: 'var(--awp-surface)', color: 'var(--awp-text)' }}
                />
              </div>
            )}

            {scheduleType === 'monthly' && (
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--awp-text-muted)', marginBottom: 4 }}>Month</label>
                <input
                  type="month" value={schedMonth} onChange={e => setSchedMonth(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--awp-border)', fontSize: 14, background: 'var(--awp-surface)', color: 'var(--awp-text)' }}
                />
              </div>
            )}

            {scheduleType === 'custom' && (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--awp-text-muted)', marginBottom: 4 }}>Start Date</label>
                  <input
                    type="date" value={schedStartDate} onChange={e => setSchedStartDate(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--awp-border)', fontSize: 14, background: 'var(--awp-surface)', color: 'var(--awp-text)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--awp-text-muted)', marginBottom: 4 }}>End Date</label>
                  <input
                    type="date" value={schedEndDate} onChange={e => setSchedEndDate(e.target.value)}
                    min={schedStartDate || undefined}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--awp-border)', fontSize: 14, background: 'var(--awp-surface)', color: 'var(--awp-text)' }}
                  />
                </div>
              </>
            )}

            {datePreview && (
              <div style={{ padding: '8px 14px', background: 'var(--awp-surface-2)', borderRadius: 8, border: '1px solid var(--awp-border)' }}>
                <span style={{ fontSize: 11, color: 'var(--awp-text-subtle)', display: 'block', marginBottom: 2 }}>Period</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--awp-text-strong)' }}>{datePreview}</span>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className="admin-btn admin-btn--edit"
              onClick={() => handleSchedule(true)}
              style={{ fontSize: 13, padding: '8px 18px' }}
              title="Adds all eligible videos to the pool automatically"
            >
              Auto-Schedule All
            </button>
            <button
              className="admin-btn admin-btn--approve"
              onClick={() => handleSchedule(false)}
              disabled={selectedIds.size === 0}
              style={{ fontSize: 13, padding: '8px 18px' }}
            >
              {selectedIds.size > 0
                ? `Schedule ${selectedIds.size} Selected Video${selectedIds.size > 1 ? 's' : ''}`
                : 'Select Videos Below First'}
            </button>
            {selectedIds.size > 0 && (
              <span style={{ fontSize: 12, color: 'var(--awp-text-subtle)' }}>
                {selectedIds.size} video{selectedIds.size > 1 ? 's' : ''} selected
                <button
                  onClick={() => setSelectedIds(new Set())}
                  style={{ marginLeft: 6, background: 'none', border: 'none', color: 'var(--awp-text-subtle)', cursor: 'pointer', fontSize: 12, padding: 0 }}
                >
                  ✕ clear
                </button>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Section 4: Current & upcoming schedules ───────────────────── */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div className="admin-card-header"><span>Schedules</span></div>
        <div className="admin-card-body">
          {scheduleLoading ? <Preloader /> : (
            <>
              {/* Active schedule */}
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--awp-text-subtle)', margin: '0 0 10px' }}>Active Now</p>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: upcomingSchedules.length > 0 ? 24 : 0 }}>
                {activeSchedule ? (
                  <ScheduleCard
                    schedule={activeSchedule}
                    label="Active"
                    labelBg="var(--awp-info-soft)"
                    labelColor="var(--awp-info)"
                  />
                ) : (
                  <div style={{ flex: '1 1 280px', background: 'var(--awp-surface-2)', borderRadius: 10, padding: 16, border: '1px dashed var(--awp-border)' }}>
                    <p style={{ fontSize: 13, color: 'var(--awp-text-subtle)', margin: 0 }}>No active schedule. Create one above.</p>
                  </div>
                )}
              </div>

              {/* Upcoming schedules */}
              {upcomingSchedules.length > 0 && (
                <>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--awp-text-subtle)', margin: '0 0 10px' }}>Upcoming</p>
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    {upcomingSchedules.map(s => (
                      <ScheduleCard
                        key={s.id}
                        schedule={s}
                        label="Upcoming"
                        labelBg="var(--awp-success-soft)"
                        labelColor="var(--awp-success)"
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Section 5: Active period user assignments ──────────────────── */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div className="admin-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span>
            Active Period Assignments
            {assignmentsSchedule && (
              <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--awp-text-muted)', marginLeft: 8 }}>
                ({assignments.length} assigned · {totalSlots} total slots available)
              </span>
            )}
          </span>
          <button className="admin-btn admin-btn--edit" onClick={fetchAssignments} style={{ fontSize: 12, padding: '5px 12px' }}>
            Refresh
          </button>
        </div>
        <div className="admin-card-body admin-table-wrap">
          {assignmentsLoading ? <Preloader /> : !assignmentsSchedule ? (
            <div className="admin-empty">No active schedule for the current period.</div>
          ) : assignments.length === 0 ? (
            <div className="admin-empty">No users have been assigned a video yet this period.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Video</th>
                  <th style={{ width: 90 }}>Starts At</th>
                  <th style={{ width: 100 }}>Watches</th>
                  <th style={{ width: 110 }}>Status Today</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map(a => {
                  const colors = STATUS_COLORS[a.today_status] || STATUS_COLORS.not_started;
                  return (
                    <tr key={a.user_id}>
                      <td style={{ fontSize: 13 }}>{a.username}</td>
                      <td style={{ fontSize: 12, maxWidth: 220 }}>
                        <span title={a.video_title}>{a.video_title || '—'}</span>
                        {a.video_id && <span style={{ color: 'var(--awp-text-subtle)', marginLeft: 6 }}>({a.video_id})</span>}
                      </td>
                      <td style={{ fontSize: 13 }}>{a.start_time_fmt}</td>
                      <td style={{ fontSize: 13 }}>{fmtDuration(a.watch_duration_seconds)}</td>
                      <td>
                        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.color, textTransform: 'capitalize' }}>
                          {a.today_status.replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Section 6: Video pool table ──────────────────────────────── */}
      <div className="admin-card">
        <div className="admin-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span>
            Video Pool
            {videoPagination && <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--awp-text-muted)', marginLeft: 8 }}>({videoPagination.totalCount} videos)</span>}
            {selectedIds.size > 0 && (
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--awp-accent)', marginLeft: 10 }}>
                {selectedIds.size} selected
                <button
                  onClick={() => setSelectedIds(new Set())}
                  style={{ marginLeft: 6, background: 'none', border: 'none', color: 'var(--awp-text-subtle)', cursor: 'pointer', fontSize: 12, padding: 0 }}
                >
                  ✕ clear
                </button>
              </span>
            )}
          </span>
          {selectedIds.size > 0 && (
            <button
              className="admin-btn admin-btn--delete"
              onClick={() => setConfirmDelete('bulk')}
              style={{ fontSize: 12, padding: '5px 14px', marginRight: 'auto' }}
            >
              🗑 Delete {selectedIds.size} Selected
            </button>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--awp-text-muted)' }}>Filter by length:</label>
            <select
              value={durationFilter}
              onChange={e => { setDurationFilter(Number(e.target.value)); setVideoPage(1); }}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--awp-border)', fontSize: 13, background: 'var(--awp-surface)', color: 'var(--awp-text)' }}
            >
              <option value={0}>Any length</option>
              <option value={30}>~30 seconds</option>
              <option value={60}>~1 minute</option>
              <option value={120}>~2 minutes</option>
              <option value={300}>~5 minutes</option>
            </select>
          </div>
        </div>
        <div className="admin-card-body admin-table-wrap">
          {videosLoading ? <Preloader /> : videos.length === 0 ? (
            <div className="admin-empty">No videos found. Scrape a YouTube channel to populate the pool.</div>
          ) : (
            <>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll}
                        title="Select / deselect all on this page"
                        style={{ cursor: 'pointer', accentColor: 'var(--awp-accent)' }} />
                    </th>
                    <th style={{ width: 80 }}>Thumb</th>
                    <th>Title</th>
                    <th style={{ width: 100 }}>Length</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {videos.map(v => {
                    const checked = selectedIds.has(v.id);
                    return (
                      <tr
                        key={v.id}
                        style={{ background: checked ? 'var(--awp-surface-hover)' : undefined, cursor: 'pointer' }}
                        onClick={() => toggleSelect(v.id)}
                      >
                        <td onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={checked} onChange={() => toggleSelect(v.id)}
                            style={{ cursor: 'pointer', accentColor: 'var(--awp-accent)' }} />
                        </td>
                        <td>
                          {v.thumbnail_url
                            ? <img src={v.thumbnail_url} alt={v.title} style={{ width: 72, height: 42, objectFit: 'cover', borderRadius: 4 }} onError={e => { e.target.style.display = 'none'; }} />
                            : <span style={{ color: 'var(--awp-text-subtle)', fontSize: 12 }}>—</span>
                          }
                        </td>
                        <td style={{ fontSize: 13, maxWidth: 320 }}>
                          <span title={v.title}>{v.title}</span>
                        </td>
                        <td style={{ fontSize: 13 }}>{fmtDuration(v.duration_seconds)}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <button
                            onClick={e => { e.stopPropagation(); setConfirmDelete(v); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--awp-danger)', fontSize: 16, padding: '2px 6px', borderRadius: 4, lineHeight: 1 }}
                            title="Delete video from pool"
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {videoPagination && videoPagination.totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '12px 0 4px' }}>
                  <button className="admin-btn admin-btn--edit" disabled={videoPage <= 1}
                    onClick={() => { setVideoPage(p => p - 1); fetchVideos(videoPage - 1); }}
                    style={{ fontSize: 12, padding: '5px 12px' }}>
                    ← Prev
                  </button>
                  <span style={{ fontSize: 13, color: 'var(--awp-text-muted)' }}>Page {videoPage} of {videoPagination.totalPages}</span>
                  <button className="admin-btn admin-btn--edit" disabled={videoPage >= videoPagination.totalPages}
                    onClick={() => { setVideoPage(p => p + 1); fetchVideos(videoPage + 1); }}
                    style={{ fontSize: 12, padding: '5px 12px' }}>
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Confirmation modal ───────────────────────────────────────── */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--awp-overlay)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'var(--awp-surface)', borderRadius: 12, padding: '28px 32px', maxWidth: 420, width: '90%',
            boxShadow: 'var(--awp-shadow-lg)', border: '1px solid var(--awp-border)'
          }}>
            <div style={{ fontSize: 32, marginBottom: 12, textAlign: 'center' }}>🗑️</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, color: 'var(--awp-text-strong)', textAlign: 'center' }}>
              {confirmDelete === 'bulk'
                ? `Delete ${selectedIds.size} video${selectedIds.size !== 1 ? 's' : ''}?`
                : 'Delete this video?'}
            </h3>
            <p style={{ margin: '0 0 22px', fontSize: 13, color: 'var(--awp-text-muted)', textAlign: 'center' }}>
              {confirmDelete === 'bulk'
                ? 'All selected videos will be permanently removed from the pool.'
                : <><strong>"{confirmDelete.title.length > 60 ? confirmDelete.title.slice(0, 57) + '…' : confirmDelete.title}"</strong> will be permanently removed.</>
              }
              {' '}This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                style={{ padding: '8px 22px', fontSize: 14, borderRadius: 8, border: '1px solid var(--awp-border)', background: 'var(--awp-surface-2)', color: 'var(--awp-text-muted)', cursor: 'pointer', fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete === 'bulk' ? handleBulkDelete : handleDeleteOne}
                disabled={deleting}
                style={{ padding: '8px 22px', fontSize: 14, borderRadius: 8, border: 'none', background: 'var(--awp-danger)', color: 'var(--awp-accent-contrast)', cursor: 'pointer', fontWeight: 600, minWidth: 90, opacity: deleting ? 0.6 : 1 }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminVideoManagement;
